import {
  Alert02Icon,
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
  Loading03Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/lib/theme';

/**
 * The one <Toaster> (§6.5). Theme comes from @/lib/theme (no next-themes).
 * Bottom-right on desktop, bottom-centre above the safe area on phones, at
 * most 3 visible. Trigger toasts through @/lib/toast, which also speaks them
 * through the single announcer; sonner's own live region is switched off so
 * nothing is read twice.
 */
function silenceLiveRegion(section: HTMLElement | null) {
  section?.setAttribute('aria-live', 'off');
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { resolved } = useTheme();
  const isMobile = useIsMobile();

  return (
    <Sonner
      ref={silenceLiveRegion}
      theme={resolved}
      position={isMobile ? 'bottom-center' : 'bottom-right'}
      visibleToasts={3}
      offset={16}
      mobileOffset={{
        bottom: 'calc(16px + env(safe-area-inset-bottom))',
        left: 16,
        right: 16,
      }}
      containerAriaLabel="Notifications"
      className="toaster group"
      icons={{
        success: (
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            strokeWidth={1.5}
            className="size-4 text-success-ink"
          />
        ),
        info: (
          <HugeiconsIcon
            icon={InformationCircleIcon}
            strokeWidth={1.5}
            className="size-4 text-info-ink"
          />
        ),
        warning: (
          <HugeiconsIcon
            icon={Alert02Icon}
            strokeWidth={1.5}
            className="size-4 text-warning-ink"
          />
        ),
        error: (
          <HugeiconsIcon
            icon={AlertCircleIcon}
            strokeWidth={1.5}
            className="size-4 text-danger-ink"
          />
        ),
        loading: (
          <HugeiconsIcon
            icon={Loading03Icon}
            strokeWidth={1.5}
            className="size-4 animate-spin motion-reduce:animate-none"
          />
        ),
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius-lg)',
          zIndex: 'var(--z-toast)',
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: 'cn-toast font-sans text-base shadow-toast',
          description: 'text-sm text-muted-foreground',
          actionButton: 'font-medium',
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
